import React from 'react';
import { ArrowRight, CheckCircle2, Code2, Smartphone, Cpu, Layers, Cloud, BarChart3 } from 'lucide-react';
import { servicesData, mobileAppSubServices } from '../data/servicesData';
import { DigitalMarketingPlans } from '../components/home/DigitalMarketingPlans';
import { ServiceItem } from '../types';

interface ServicesPageProps {
  onSelectService: (slug: string) => void;
  onOpenConsultation: (serviceName?: string) => void;
}

export const ServicesPage: React.FC<ServicesPageProps> = ({ onSelectService, onOpenConsultation }) => {
  const getIcon = (name: string) => {
    const iconClass = "w-6 h-6 text-white";
    switch (name) {
      case 'Code2': return <Code2 className={iconClass} />;
      case 'Smartphone': return <Smartphone className={iconClass} />;
      case 'Cpu': return <Cpu className={iconClass} />;
      case 'Layers': return <Layers className={iconClass} />;
      case 'Cloud': return <Cloud className={iconClass} />;
      case 'BarChart3':
      default:
        return <BarChart3 className={iconClass} />;
    }
  };

  const getAccentBg = (color: ServiceItem['accentColor']) => {
    switch (color) {
      case 'purple': return 'bg-[#7B2EFF]';
      case 'blue': return 'bg-[#159DE8]';
      case 'orange': return 'bg-[#FF851B]';
      case 'green': return 'bg-[#00C853]';
      case 'coral':
      default:
        return 'bg-[#FF5A36]';
    }
  };

  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-16">
      
      {/* Page Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <span>ENGINEERING & DIGITAL GROWTH</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            Comprehensive Digital Capabilities Built for Scale
          </h1>
          <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
            From modern cloud frontends and AI agents to cross-platform mobile apps and multi-tenant ERP suites, we engineer robust digital products tailored to your commercial vision.
          </p>
        </div>
      </section>

      {/* Detailed Services Breakdown */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {servicesData.map((s) => (
            <div
              key={s.id}
              className="p-8 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8]/50 transition-all duration-300 shadow-card-dark flex flex-col justify-between group"
            >
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getAccentBg(s.accentColor)} shadow-md`}>
                    {getIcon(s.iconName)}
                  </div>
                  <span className="text-xs font-mono uppercase tracking-widest text-[#8FA0B8]">
                    {s.accentColor.toUpperCase()} DOMAIN
                  </span>
                </div>

                <div>
                  <h2 className="text-xl font-bold text-white font-display group-hover:text-[#FF851B] transition-colors">
                    {s.title}
                  </h2>
                  <p className="text-xs sm:text-sm text-[#8FA0B8] mt-2 leading-relaxed">
                    {s.fullDescription}
                  </p>
                </div>

                <div className="space-y-2 pt-2 border-t border-[#1b2f4a]/50">
                  <span className="text-xs uppercase tracking-wider font-semibold text-white block">Key Benefits:</span>
                  {s.benefits.slice(0, 3).map((b, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-[#D5DFEC]">
                      <CheckCircle2 size={14} className="text-[#00C853] shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-[#1b2f4a] flex items-center justify-between">
                <button
                  onClick={() => onSelectService(s.slug)}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#159DE8] group-hover:text-[#FF851B] transition-colors"
                >
                  <span>Explore Service Deep-Dive</span>
                  <ArrowRight size={14} />
                </button>
                <span className="text-[11px] text-[#8FA0B8]">Production Ready</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sub-services Matrix */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-10 rounded-3xl bg-[#050A13] border border-[#1b2f4a]">
          <div className="max-w-2xl mb-8 space-y-2">
            <h2 className="text-xl sm:text-2xl font-bold text-white font-display">Specialized Mobile App Engineering</h2>
            <p className="text-xs sm:text-sm text-[#8FA0B8]">
              Delivering high-performance mobile interfaces across iOS, Android, and hybrid web shells.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mobileAppSubServices.map((m, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-[#071426] border border-[#1b2f4a]">
                <h3 className="text-sm font-bold text-white mb-1">{m.title}</h3>
                <p className="text-xs text-[#8FA0B8] leading-relaxed">{m.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Marketing Keyword-Based Plans Section */}
      <DigitalMarketingPlans onSelectPlan={(plan) => onOpenConsultation(`Digital Marketing - ${plan}`)} />

      {/* Consultation Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#0B2342] to-[#071426] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white font-display">Need a tailored solution for your organization?</h2>
            <p className="text-sm text-[#D5DFEC]">Schedule a scoping session with our lead architects.</p>
          </div>
          <button
            onClick={() => onOpenConsultation()}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-sm font-bold shadow-lg hover:shadow-glow-orange transition-all shrink-0 flex items-center gap-2"
          >
            <span>Book a Consultation</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

    </div>
  );
};
